import { useState } from 'react';
import {
  AlertDialog,
  Button,
  Description,
  Dropdown,
  Label,
  Modal,
  Spinner,
  Table,
  toast,
} from '@heroui/react';
import {
  IconApps,
  IconBrandWechat,
  IconCurrencyDollar,
  IconDots,
  IconPackage,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { SkuEditorModal, type SkuFormItem } from './SkuEditorModal';
import {
  SkuPriceRuleEditorModal,
  type SkuPriceRuleFormItem,
} from './SkuPriceRuleEditorModal';
import { SkuEntitlementTemplateModal } from './SkuEntitlementTemplateModal';
import { SkuAppModal } from './SkuAppModal';
import { SkuVirtualItemModal } from './SkuVirtualItemModal';

interface SkuListModalProps {
  open: boolean;
  productId?: string | null;
  productName?: string;
  productType?: string | null;
  onCancel: () => void;
}

type SkuRow = {
  id: string;
  code?: string | null;
  name?: string | null;
  saleType?: string | null;
  billingPeriodUnit?: string | null;
  billingPeriodCount?: number | null;
  defaultSeatQuantity?: number | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  tier?: number | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string | Date | null;
  skuPriceRule?: SkuPriceRuleFormItem[] | null;
};

const SALE_TYPE_LABELS: Record<string, string> = {
  SUBSCRIPTION: '订阅型',
  ONE_TIME: '一次性',
};

const BILLING_PERIOD_LABELS: Record<string, string> = {
  MONTH: '月',
  YEAR: '年',
  NONE: '无',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '在售',
  INACTIVE: '停售',
};

function dash(value: unknown): string {
  if (value == null || value === '') return '-';
  return String(value);
}

function formatYuanFromFen(value: number | string | null | undefined): string {
  if (value == null || value === '') return '-';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '-';
  const yuan = n / 100;
  return Number.isInteger(yuan) ? `${yuan}元` : `${yuan.toFixed(2)}元`;
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '-';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function primaryPriceRule(sku: SkuRow | null | undefined): SkuPriceRuleFormItem | null {
  if (!sku) return null;
  const rules = [...(sku.skuPriceRule ?? [])].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
  );
  return rules[0] ?? null;
}

export function SkuListModal({
  open,
  productId,
  productName,
  productType,
  onCancel,
}: SkuListModalProps) {
  const utils = trpc.useUtils();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSku, setEditingSku] = useState<SkuFormItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [skuToDelete, setSkuToDelete] = useState<SkuRow | null>(null);
  const [priceRuleOpen, setPriceRuleOpen] = useState(false);
  const [priceRuleSku, setPriceRuleSku] = useState<SkuRow | null>(null);
  const [entitlementOpen, setEntitlementOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const [virtualItemOpen, setVirtualItemOpen] = useState(false);
  const [actionSku, setActionSku] = useState<SkuRow | null>(null);

  const { data: skus = [], isLoading } = trpc.bizProduct.sku.listAll.useQuery(
    { productId: productId ?? undefined, sortBy: 'code', sortOrder: 'asc' },
    { enabled: open && Boolean(productId) }
  );

  const deleteMutation = trpc.bizProduct.sku.delete.useMutation({
    onSuccess: () => {
      toast.success('SKU 已删除');
      setDeleteOpen(false);
      setSkuToDelete(null);
      void utils.bizProduct.sku.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
  });

  const rows = skus as SkuRow[];

  const handleCreate = () => {
    setEditingSku(null);
    setEditorOpen(true);
  };

  const handleEdit = (sku: SkuRow) => {
    setEditingSku(sku);
    setEditorOpen(true);
  };

  const handleDelete = (sku: SkuRow) => {
    setSkuToDelete(sku);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!skuToDelete?.id) return;
    await deleteMutation.mutateAsync({ id: skuToDelete.id });
  };

  const handlePriceRule = (sku: SkuRow) => {
    setPriceRuleSku(sku);
    setPriceRuleOpen(true);
  };

  const openActionModal = (sku: SkuRow, kind: 'entitlement' | 'app' | 'virtual') => {
    setActionSku(sku);
    if (kind === 'entitlement') setEntitlementOpen(true);
    if (kind === 'app') setAppOpen(true);
    if (kind === 'virtual') setVirtualItemOpen(true);
  };

  return (
    <>
      <Modal isOpen={open} onOpenChange={(next) => !next && onCancel()}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" size="cover" scroll="inside">
            <Modal.Dialog>
              <Modal.CloseTrigger />
              <Modal.Header className="gap-1">
                <Modal.Heading>SKU管理</Modal.Heading>
                <Description className="text-sm">
                  当前产品：{productName || '-'}
                </Description>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-medium">SKU列表</h3>
                  <Button
                    size="sm"
                    isDisabled={!productId}
                    onPress={handleCreate}
                  >
                    <IconPlus className="size-4" stroke={1.5} />
                    新建
                  </Button>
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <p className="text-sm text-muted">暂无 SKU</p>
                ) : (
                  <Table>
                    <Table.ScrollContainer>
                      <Table.Content aria-label="SKU 列表" className="min-w-[1200px]">
                        <Table.Header>
                          <Table.Column isRowHeader>SKU代码</Table.Column>
                          <Table.Column>SKU名称</Table.Column>
                          <Table.Column>售卖方式</Table.Column>
                          <Table.Column>计费周期</Table.Column>
                          <Table.Column>单价</Table.Column>
                          <Table.Column>基础周期数量</Table.Column>
                          <Table.Column>默认席位数</Table.Column>
                          <Table.Column>最小购买量</Table.Column>
                          <Table.Column>最大购买量</Table.Column>
                          <Table.Column>状态</Table.Column>
                          <Table.Column>档位</Table.Column>
                          <Table.Column>创建时间</Table.Column>
                          <Table.Column>操作</Table.Column>
                        </Table.Header>
                        <Table.Body>
                          {rows.map((sku) => {
                            const rule = primaryPriceRule(sku);
                            return (
                              <Table.Row key={String(sku.id)} id={String(sku.id)}>
                                <Table.Cell>{dash(sku.code)}</Table.Cell>
                                <Table.Cell>{dash(sku.name)}</Table.Cell>
                                <Table.Cell>
                                  {dash(SALE_TYPE_LABELS[sku.saleType ?? ''] ?? sku.saleType)}
                                </Table.Cell>
                                <Table.Cell>
                                  {dash(
                                    BILLING_PERIOD_LABELS[sku.billingPeriodUnit ?? ''] ??
                                      sku.billingPeriodUnit
                                  )}
                                </Table.Cell>
                                <Table.Cell>{formatYuanFromFen(rule?.unitPrice)}</Table.Cell>
                                <Table.Cell>{dash(sku.billingPeriodCount)}</Table.Cell>
                                <Table.Cell>{dash(sku.defaultSeatQuantity)}</Table.Cell>
                                <Table.Cell>{dash(sku.minQuantity)}</Table.Cell>
                                <Table.Cell>{dash(sku.maxQuantity)}</Table.Cell>
                                <Table.Cell>
                                  {dash(STATUS_LABELS[sku.status ?? ''] ?? sku.status)}
                                </Table.Cell>
                                <Table.Cell>{dash(sku.tier)}</Table.Cell>
                                <Table.Cell>{formatDateTime(sku.createdAt)}</Table.Cell>
                                <Table.Cell>
                                  <Dropdown>
                                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                                      <IconDots className="size-4" stroke={1.5} />
                                    </Button>
                                    <Dropdown.Popover className="min-w-[160px]" placement="bottom end">
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          id="edit"
                                          textValue="编辑"
                                          onAction={() => handleEdit(sku)}
                                        >
                                          <IconPencil className="size-4 shrink-0 text-muted" stroke={1.5} />
                                          <Label>编辑</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="delete"
                                          textValue="删除"
                                          variant="danger"
                                          onAction={() => handleDelete(sku)}
                                        >
                                          <IconTrash className="size-4 shrink-0" stroke={1.5} />
                                          <Label>删除</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="price"
                                          textValue="定价规则"
                                          onAction={() => handlePriceRule(sku)}
                                        >
                                          <IconCurrencyDollar
                                            className="size-4 shrink-0 text-muted"
                                            stroke={1.5}
                                          />
                                          <Label>定价规则</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="entitlement"
                                          textValue="权益配置"
                                          onAction={() => openActionModal(sku, 'entitlement')}
                                        >
                                          <IconPackage
                                            className="size-4 shrink-0 text-muted"
                                            stroke={1.5}
                                          />
                                          <Label>权益配置</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="apps"
                                          textValue="关联应用"
                                          onAction={() => openActionModal(sku, 'app')}
                                        >
                                          <IconApps className="size-4 shrink-0 text-muted" stroke={1.5} />
                                          <Label>关联应用</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="virtual"
                                          textValue="虚拟道具"
                                          onAction={() => openActionModal(sku, 'virtual')}
                                        >
                                          <IconBrandWechat
                                            className="size-4 shrink-0 text-muted"
                                            stroke={1.5}
                                          />
                                          <Label>虚拟道具</Label>
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown.Popover>
                                  </Dropdown>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Content>
                    </Table.ScrollContainer>
                  </Table>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onPress={onCancel}>
                  关闭
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      {productId ? (
        <SkuEditorModal
          open={editorOpen}
          productId={productId}
          productType={productType}
          sku={editingSku}
          onCancel={() => {
            setEditorOpen(false);
            setEditingSku(null);
          }}
          onSuccess={() => {
            setEditorOpen(false);
            setEditingSku(null);
          }}
        />
      ) : null}

      {priceRuleSku?.id ? (
        <SkuPriceRuleEditorModal
          open={priceRuleOpen}
          skuId={String(priceRuleSku.id)}
          skuName={priceRuleSku.name ?? undefined}
          priceRule={primaryPriceRule(priceRuleSku)}
          onCancel={() => {
            setPriceRuleOpen(false);
            setPriceRuleSku(null);
          }}
          onSuccess={() => {
            setPriceRuleOpen(false);
            setPriceRuleSku(null);
          }}
        />
      ) : null}

      <SkuEntitlementTemplateModal
        open={entitlementOpen}
        skuId={actionSku?.id}
        skuName={actionSku?.name ?? undefined}
        onCancel={() => {
          setEntitlementOpen(false);
          setActionSku(null);
        }}
      />
      <SkuAppModal
        open={appOpen}
        skuId={actionSku?.id}
        skuName={actionSku?.name ?? undefined}
        onCancel={() => {
          setAppOpen(false);
          setActionSku(null);
        }}
      />
      <SkuVirtualItemModal
        open={virtualItemOpen}
        skuId={actionSku?.id}
        skuName={actionSku?.name ?? undefined}
        unitPriceFen={(() => {
          const raw = primaryPriceRule(actionSku)?.unitPrice;
          if (raw == null || raw === '') return null;
          const n = typeof raw === 'string' ? Number(raw) : raw;
          return Number.isFinite(n) ? n : null;
        })()}
        onCancel={() => {
          setVirtualItemOpen(false);
          setActionSku(null);
        }}
      />

      <AlertDialog isOpen={deleteOpen} onOpenChange={setDeleteOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除{skuToDelete?.name ?? skuToDelete?.code ?? '该 SKU'}吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteOpen(false);
                    setSkuToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => void confirmDelete()}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
